import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import { Button, Stack, TextField, Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { Dayjs } from 'dayjs'
import { useEffect } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useBulkPaymentStepperContext } from '../../contexts/BulkPaymentStepperContext'
import { formatFormInputCurrencyString } from '../../utils/currencyFormatter'
import { parseCurrencyString } from '../../utils/currencyParser'
import Footer from './Footer'

export default function Step2() {
  const { vendor, paymentTotal, setPaymentTotal, setActiveStep, dateReceived, setDateReceived } =
    useBulkPaymentStepperContext()

  interface IFormInput {
    amount: string
    dateReceived: Dayjs
  }
  const {
    control,
    handleSubmit,
    setValue,
    formState,
    watch,
    trigger: triggerValidation,
  } = useForm<IFormInput>({
    defaultValues: {
      amount: (() => {
        if (!vendor) return ''
        return formatFormInputCurrencyString(vendor.outstandingCommission)
      })(),
      dateReceived,
    },
    mode: 'onBlur',
  })
  const amount = watch('amount')
  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    setPaymentTotal(parseCurrencyString(data.amount))
    setDateReceived(data.dateReceived)
    setActiveStep(2)
  }

  const handleAmountBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const formatted = formatFormInputCurrencyString(e.target.value)
    if (!formatted) return
    setValue('amount', formatted)
    triggerValidation('amount')
  }

  useEffect(() => {
    const n = Number.parseFloat(amount.replace(/[^0-9.]/g, ''))
    if (Number.isNaN(n)) return
    if (n <= 0) return
    setPaymentTotal(n)
  }, [amount, setPaymentTotal])

  return (
    <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        <Stack spacing={2} maxWidth="50%" mx="auto" alignSelf="center">
          <Typography variant="body2" mb={2}>
            Next, enter the total payment amount you've received from the vendor.
          </Typography>
          <Controller
            name="amount"
            control={control}
            rules={{
              required: true,
              validate: (v) => {
                if (!v) return 'Amount is required'
                const n = Number.parseFloat(v as any as string)
                if (Number.isNaN(n)) {
                  return 'Amount must be a number'
                }
                return true
              },
              onBlur: handleAmountBlur,
            }}
            render={({ field, fieldState }) => {
              return (
                <TextField
                  label="Amount ($) *"
                  {...field}
                  // disabled={fetching}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message || 'The payment amount received'}
                  slotProps={{
                    input: {
                      startAdornment: '$',
                      required: true,
                    },
                  }}
                />
              )
            }}
          />
          <Controller
            name="dateReceived"
            control={control}
            render={({ field, fieldState }) => {
              return (
                <DatePicker
                  label="Date Received"
                  {...field}
                  disableFuture
                  slotProps={{
                    textField: {
                      error: !!fieldState.error,
                      helperText: fieldState.error?.message || 'When you received the payment',
                    },
                    field: {
                      clearable: true,
                    },
                  }}
                />
              )
            }}
          />
        </Stack>
        <Footer>
          <Button
            variant="contained"
            color="primary"
            sx={{ flexBasis: '50%' }}
            onClick={() => setActiveStep(0)}
            endIcon={<NavigateBeforeIcon />}
          >
            Back
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ flexBasis: '50%' }}
            disabled={paymentTotal === undefined || paymentTotal <= 0}
            endIcon={<NavigateNextIcon />}
          >
            Next
          </Button>
        </Footer>
      </Stack>
    </form>
  )
}
